import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
    }
    return Promise.reject(error);
  }
);

// Authentication APIs
export const authAPI = {
  login: async (email, password) => {
    try {
      const res = await api.post('/auth/login', { email, password });
      const { data } = res.data;

      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data));

      return { success: true, data: { token: data.token, user: data } };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || 'Login failed',
      };
    }
  },

  logout: async () => {
    // No API call — just clear localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    return { success: true };
  },

  resetPassword: async (email, otp, password) => {
    try {
      const response = await api.post('/auth/reset-password', { email, otp, password });
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to reset password'
      };
    }
  },

  verifyOtp: async (email, otp, type) => {
    try {
      const response = await api.post('/auth/verify', { email, otp, type });
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to verify OTP'
      };
    }
  },

  requestOtp: async (email, type) => {
    try {
      const response = await api.post('/auth/request-code', { email, type });
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('requestOtp error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to request OTP',
      };
    }
  },

  autologin: async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return { success: false, error: 'No auth token found' };
      }

      const response = await api.get('/auth/auto-login');

      const user = response.data.data;

      localStorage.setItem('user', JSON.stringify(user));
      return { success: true, data: user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Auto-login failed'
      };
    }
  },

  signUp: async (email, password) => {
    try {
      const response = await api.post('/auth/signup', { email, password });
      return { success: true, data: response.data.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Sign up failed'
      };
    }
  },
}


// Course APIs
export const CourseAPI = {
  overview: async () => {
    try {
      const response = await api.get('/course/overview');
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch courses'
      };
    }
  },

  getProgressById: async (id) => {
    try {
      const response = await api.get(`/course/progress/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch course progress'
      };
    }
  },

  updateProgress: async (courseRef, progress, timeSpentInMinutes, numberOfSlides, currentSlide, courseTitle) => {
    try {
      const response = await api.post(`/course/update-progress`, { courseRef, progress, timeSpentInMinutes, numberOfSlides, currentSlide, courseTitle });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch slides'
      };
    }
  },
  getTimeline: async () => {
    try {
      const response = await api.get('/course/timeline');
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch course timeline'
      };
    }
  }
};

// Quiz APIs
export const quizAPI = {
  getQuizByID: async (quizId) => {
    try {
      const response = await api.get(`/quiz/${quizId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch quiz'
      };
    }
  },

  submitQuiz: async (answers) => {
    try {
      const response = await api.post(`/quiz/submit`, answers);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to submit quiz'
      };
    }
  },
};

// Progress APIs
export const progressAPI = {
  getUserProgress: async (userId) => {
    try {
      const response = await api.get(`/progress/${userId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch user progress'
      };
    }
  },

  updateProgress: async (userId, chapterId, progressData) => {
    try {
      const response = await api.put(`/progress/${userId}/${chapterId}`, progressData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update progress'
      };
    }
  },

  getChapterProgress: async (userId, chapterId) => {
    try {
      const response = await api.get(`/progress/${userId}/${chapterId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch chapter progress'
      };
    }
  }
};

// User preference APIs
export const userAPI = {
  updatePreferences: async (userId, preferences) => {
    try {
      const response = await api.put(`/users/${userId}/preferences`, preferences);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update preferences'
      };
    }
  },

  getPreferences: async (userId) => {
    try {
      const response = await api.get(`/users/${userId}/preferences`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch preferences'
      };
    }
  }
};

export default api;